import React, { useCallback, useMemo, useState } from 'react'
import { Table, Tooltip } from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  FilePdfOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import { TableSection } from 'portal/components/RecordsModules/common/StyledComponents'
import { DocumentViewer } from './DocumentViewer'
import {
  VerificationCheck,
  VerificationCheckSource
} from 'portal/store/records/designMatchesV2/types'

type OpenSourceDocument = {
  documentName: string
  documentURL: string
  documentPageNumber: number
}

const isMatchStatus = (status: string) =>
  `${status || ''}`.trim().toLowerCase() === 'match'

const STATUS_DISPLAY: Record<string, string> = {
  nomatch: 'no match',
  cannotcompare: 'cannot compare'
}

export function DeliveryVerificationChecks({
  checks
}: {
  checks: VerificationCheck[]
}) {
  const [openSourceDocument, setOpenSourceDocument] =
    useState<OpenSourceDocument>()

  const { matchCount, totalCount } = useMemo(
    () => ({
      matchCount: checks.filter(c => isMatchStatus(c.status)).length,
      totalCount: checks.length
    }),
    [checks]
  )

  const renderSource = useCallback((record: VerificationCheck) => {
    const sources = Array.isArray(record?.sources) ? record.sources : []

    if (sources.length === 0) {
      return <SourceText>{record?.source || '-'}</SourceText>
    }

    return (
      <SourceList>
        {sources.map((source: VerificationCheckSource, index: number) => {
          const documentName =
            `${source?.documentName || ''}`.trim() ||
            record?.source ||
            'Document'
          const documentLink = `${source?.documentLink || ''}`.trim()
          const documentPageNumber = Number(source?.documentPageNumber || 1)

          if (!documentLink) {
            return (
              <SourceText key={`${record.id}-source-${index}`}>
                {documentName}
              </SourceText>
            )
          }

          return (
            <LinkedSource key={`${record.id}-source-${index}`}>
              <Tooltip
                title={`Preview document: ${documentName} (page ${documentPageNumber})`}
              >
                <SourcePreviewButton
                  type="button"
                  aria-label={`Preview ${documentName}`}
                  onClick={() =>
                    setOpenSourceDocument({
                      documentName,
                      documentURL: documentLink,
                      documentPageNumber
                    })
                  }
                >
                  <FilePdfOutlined />
                </SourcePreviewButton>
              </Tooltip>
            </LinkedSource>
          )
        })}
      </SourceList>
    )
  }, [])

  const columns = useMemo(
    () => [
      {
        key: 'condition',
        dataIndex: 'condition',
        title: 'Condition',
        render: (condition: string) => {
          const s = `${condition || ''}`.trim()
          return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
        }
      },
      {
        key: 'required',
        dataIndex: 'required',
        title: 'Required',
        width: '30%'
      },
      {
        key: 'delivered',
        dataIndex: 'delivered',
        title: 'Delivered',
        width: '30%',
        render: (delivered: string, record: VerificationCheck) =>
          !isMatchStatus(record?.status) ? (
            <DeliveredMismatchText>{delivered}</DeliveredMismatchText>
          ) : (
            delivered
          )
      },
      {
        key: 'status',
        dataIndex: 'status',
        title: 'Status',
        render: (status: string) => {
          const isMatch = isMatchStatus(status)
          const normalised = `${status || ''}`.trim().toLowerCase()
          const text = STATUS_DISPLAY[normalised] ?? (normalised || 'review')

          return (
            <StatusCapsule $isMatch={isMatch}>
              {isMatch ? <CheckOutlined /> : <CloseOutlined />}
              <span>{text}</span>
            </StatusCapsule>
          )
        }
      },
      {
        key: 'source',
        dataIndex: 'source',
        title: 'Source',
        render: (_value: string, record: VerificationCheck) =>
          renderSource(record)
      }
    ],
    [renderSource]
  )

  return (
    <TableSection>
      {totalCount > 0 && (
        <ConditionsSummary $allMet={matchCount === totalCount}>
          {matchCount} of {totalCount} conditions met
        </ConditionsSummary>
      )}
      <VerificationChecksTableContainer>
        <Table
          id="verification-checks-table"
          dataSource={checks}
          rowKey="id"
          columns={columns}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No design matches' }}
        />
      </VerificationChecksTableContainer>
      {openSourceDocument && (
        <DocumentViewer
          documentName={openSourceDocument.documentName}
          documentURL={openSourceDocument.documentURL}
          documentPageNumber={openSourceDocument.documentPageNumber || 1}
          onCancel={() => setOpenSourceDocument(undefined)}
        />
      )}
    </TableSection>
  )
}

const ConditionsSummary = styled.div<{ $allMet: boolean }>`
  text-align: right;
  font-weight: 600;
  color: ${({ $allMet }) => ($allMet ? colours.GREEN : colours.ORANGE)};
  margin: 0.25rem 1rem;
`

const VerificationChecksTableContainer = styled.div`
  margin: 0 1rem 1rem 0;
`

const DeliveredMismatchText = styled.span`
  color: ${colours.ORANGE_TEXT};
  font-weight: 500;
`

const StatusCapsule = styled.span<{ $isMatch: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: 999px;
  padding: 0.125rem 0.625rem;
  font-weight: 500;
  background-color: ${({ $isMatch }) =>
    $isMatch ? colours.PALE_GREEN : colours.CONSTRUCTION_ORANGE_BACKGROUND};
  color: ${({ $isMatch }) =>
    $isMatch ? colours.SUCCESS : colours.ORANGE_TEXT};
`

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const LinkedSource = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
`

const SourcePreviewButton = styled.button`
  border: 0;
  background: transparent;
  color: ${colours.TEAL};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 0;

  &:hover {
    color: ${colours.TEAL};
  }
`

const SourceText = styled.span`
  color: ${colours.MID_GREY};
`
